import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { ConfigCampania, ConfigCampaniaCreate, ConfigCampaniaUpdate, Marca } from '@/modules/multichannel/types/configuracion'

interface ConfiguracionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  configuracion: ConfigCampania | null
  marcas: Marca[]
  onSubmit: (data: ConfigCampaniaCreate) => void
  onUpdate: (id: number, data: ConfigCampaniaUpdate) => void
  isSubmitting: boolean
}

interface FormData {
  marca_secundaria_id: string
  nombre_campania: string
  nombre_encargado: string
  remitente: string
  target: string
  base_segmentada: string
}

export function ConfiguracionFormDialog({
  open, onOpenChange, configuracion, marcas, onSubmit, onUpdate, isSubmitting,
}: ConfiguracionFormDialogProps) {
  const isEdit = !!configuracion

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { marca_secundaria_id: '', nombre_campania: '', nombre_encargado: '', remitente: '', target: '', base_segmentada: '' },
  })

  useEffect(() => {
    if (open && configuracion) {
      reset({
        marca_secundaria_id: String(configuracion.marca_secundaria_id),
        nombre_campania: configuracion.nombre_campania,
        nombre_encargado: configuracion.nombre_encargado,
        remitente: configuracion.remitente,
        target: configuracion.target,
        base_segmentada: configuracion.base_segmentada,
      })
    } else if (!open) {
      reset({ marca_secundaria_id: '', nombre_campania: '', nombre_encargado: '', remitente: '', target: '', base_segmentada: '' })
    }
  }, [open, configuracion, reset])

  const onFormSubmit = (data: FormData) => {
    if (isEdit && configuracion) {
      onUpdate(configuracion.id, {
        marca_secundaria_id: Number(data.marca_secundaria_id),
        nombre_campania: data.nombre_campania,
        nombre_encargado: data.nombre_encargado,
        remitente: data.remitente,
        target: data.target,
        base_segmentada: data.base_segmentada,
      })
    } else {
      onSubmit({
        marca_secundaria_id: Number(data.marca_secundaria_id),
        nombre_campania: data.nombre_campania,
        nombre_encargado: data.nombre_encargado,
        remitente: data.remitente,
        target: data.target,
        base_segmentada: data.base_segmentada,
      })
    }
  }

  const activeMarcas = marcas.filter(m => m.activo)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Configuracion' : 'Nueva Configuracion'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modifica los datos de la configuracion de campana.' : 'Define una nueva configuracion de campana.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="marca_secundaria_id">Marca <span className="text-destructive">*</span></Label>
            <select
              id="marca_secundaria_id"
              {...register('marca_secundaria_id', { required: 'Selecciona una marca' })}
              disabled={isSubmitting}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Seleccionar marca...</option>
              {activeMarcas.map(m => (
                <option key={m.id} value={m.id}>{m.marca_principal_nombre} - {m.nombre}</option>
              ))}
            </select>
            {errors.marca_secundaria_id && <p className="text-sm text-destructive">{errors.marca_secundaria_id.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nombre_campania">Nombre Campana <span className="text-destructive">*</span></Label>
            <Input id="nombre_campania" {...register('nombre_campania', { required: 'Requerido' })} disabled={isSubmitting} />
            {errors.nombre_campania && <p className="text-sm text-destructive">{errors.nombre_campania.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nombre_encargado">Encargado <span className="text-destructive">*</span></Label>
            <Input id="nombre_encargado" {...register('nombre_encargado', { required: 'Requerido' })} disabled={isSubmitting} />
            {errors.nombre_encargado && <p className="text-sm text-destructive">{errors.nombre_encargado.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="remitente">Remitente <span className="text-destructive">*</span></Label>
              <Input id="remitente" {...register('remitente', { required: 'Requerido' })} disabled={isSubmitting} />
              {errors.remitente && <p className="text-sm text-destructive">{errors.remitente.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="target">Target <span className="text-destructive">*</span></Label>
              <Input id="target" {...register('target', { required: 'Requerido' })} disabled={isSubmitting} />
              {errors.target && <p className="text-sm text-destructive">{errors.target.message}</p>}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="base_segmentada">Base Segmentada <span className="text-destructive">*</span></Label>
            <Input id="base_segmentada" {...register('base_segmentada', { required: 'Requerido' })} disabled={isSubmitting} />
            {errors.base_segmentada && <p className="text-sm text-destructive">{errors.base_segmentada.message}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
